<script setup>
// Поле написання — і у власному чаті ефіру, і в коментарях (ChatBody): текст,
// підказки смайлів, панель вибору і до пʼяти картинок (або одне відео) — за
// бажанням під спойлером.
//
// Підказки працюють як у Twitch: набираєш початок назви — під полем
// зʼявляється список, Tab підставляє перший (або вибраний стрілками).
// Enter при цьому НАДСИЛАЄ повідомлення, а не підставляє: список висить
// майже на кожне довше слово, і якби Enter його підхоплював, чат раз у раз
// відправляв би не те, що набрали. Підставити Enter-ом можна тільки те, що
// свідомо вибрали стрілками.
//
// Тригерів два, і вони не заважають один одному:
//   «:»  — підказки саме емоцій, як у 7TV: «:kek» звужує список до емоцій,
//          навіть якщо таке слово взагалі не схоже на назву смайла;
//   «@»  — підказки людей: ніки часто збігаються зі звичайними словами, і
//          підказувати їх на кожне слово означало б не підказувати нічого.
// Без тригера чат так само підказує емоції на довше за дві літери слово —
// назви емоцій пишуть просто словом, і саме так їх набирають у чаті.
//
// Картинку можна не вибирати файлом, а вставити з буфера (Ctrl+V) або
// кинути в чат мишею: скріншот моменту з ефіру інакше довелося б спершу
// кудись зберегти.

const props = defineProps({
  // Надсилання: useLiveChat повертає false, якщо зʼєднання саме зараз
  // немає, useThread — проміс, що падає з причиною. В обох випадках
  // набране лишається в полі
  send: { type: Function, required: true },
  // Завантажувач медіа з useLiveChat: повертає {id, url, mime, name}
  upload: { type: Function, required: true },
  authenticated: { type: Boolean, default: false },
  muted: { type: Boolean, default: false },
  mutedUntil: { type: String, default: null },
  connected: { type: Boolean, default: false },
  maxLength: { type: Number, default: 500 },
  // Кого підказувати після «@»: хто зараз у кімнаті плюс автори зі стрічки
  chatters: { type: Array, default: () => [] },
  // Своє імʼя — щоб не підказувати себе: до себе не звертаються
  meName: { type: String, default: '' },
  // На що відповідаємо. Тримає стрічка (ChatLiveBody) — саме там по ньому
  // клікають
  replyTo: { type: Object, default: null },
  // Останнє неприйняте повідомлення: сервер відмовив (або не відповів), і
  // набране треба повернути в поле, а не втратити
  rejected: { type: Object, default: null },
  // Скільки вкладень в одному повідомленні
  maxMedia: { type: Number, default: 5 },
  placeholder: { type: String, default: 'Написати в чат…' },
  // «/» з будь-якого місця сторінки — тільки для чату ефіру: коментарів на
  // сторінці серії кілька, і клавіша не знала б, у який із них іти
  globalHotkey: { type: Boolean, default: true },
  // Куди повернутись після входу через Twitch/Discord. Порожньо — поточна
  // сторінка
  loginRedirect: { type: String, default: '' }
})

// Гаряча клавіша може прилетіти й тоді, коли чат згорнутий, — тоді його
// спершу треба розгорнути (стан колонки живе аж у live/Chat.vue)
const emit = defineEmits(['open-chat', 'cancel-reply'])

// Стрімера підказуємо завжди — навіть коли він сам у чаті нічого не писав
// і в кімнаті його ще не видно. До нього звертаються найчастіше
const STREAMER = 'olsior'

const { load, search } = useEmotes()

const text = ref('')
// Вкладення: кожне вантажиться окремим запитом одразу після вибору, тож
// поки людина дописує текст, файли вже на сервері.
// { key, preview, video, status: 'uploading'|'done', media }
const attachments = ref([])
const spoiler = ref(false)
const sending = ref(false)
const failure = ref('')
const uploading = computed(() => attachments.value.some((item) => item.status === 'uploading'))
const ready = computed(() => attachments.value.filter((item) => item.status === 'done'))

const area = ref(null)
const fileInput = ref(null)
// Над полем тримають файл мишею — підсвічуємо, щоб було видно, що кидати
// сюди можна
const dragOver = ref(false)

// --- Підказки: смайли й згадки ---
const suggestions = ref([])
// -1 — «нічого не вибрано»: список показаний, але людина в ньому не
// ходила, тож Enter лишається надсиланням
const activeIndex = ref(-1)
// Межі слова під курсором — щоб підставити рівно його, а не перше схоже
// в рядку
let wordRange = null

// З однієї літери підказок було б пів списку, і вони перекривали б чат на
// кожне слово
const MIN_QUERY = 2
const MAX_SUGGESTIONS = 8

onMounted(load)

const closeSuggestions = () => {
  suggestions.value = []
  activeIndex.value = -1
  wordRange = null
}

/**
 * Кого можна згадати: хто зараз у кімнаті, автори зі стрічки й стрімер.
 * Себе не підказуємо — до себе не звертаються
 */
const nicks = computed(() => {
  const seen = new Set()
  const list = []

  const self = props.meName.toLowerCase()

  for (const name of [STREAMER, ...props.chatters]) {
    const key = String(name || '').toLowerCase()
    if (!key || key === self || seen.has(key)) continue
    seen.add(key)
    list.push(name)
  }

  return list
})

const searchNicks = (query, limit) => {
  const needle = query.toLowerCase()
  return nicks.value
    .filter((name) => name.toLowerCase().startsWith(needle))
    .slice(0, limit)
    .map((name) => ({ name, nick: true }))
}

const refreshSuggestions = () => {
  const el = area.value
  if (!el) return closeSuggestions()

  const caret = el.selectionStart ?? text.value.length
  const before = text.value.slice(0, caret)
  const match = before.match(/(\S+)$/)

  if (!match) return closeSuggestions()

  const word = match[1]
  // «@» і «:» — це вже вибір людини, кого саме підказувати, тож список
  // показуємо з першої ж літери після символу, а не з другої
  const mention = word.startsWith('@')
  const emoteQuery = !mention && word.startsWith(':')
  if (!mention && !emoteQuery && word.length < MIN_QUERY) return closeSuggestions()

  const found = mention
    ? searchNicks(word.slice(1), MAX_SUGGESTIONS)
    : search(emoteQuery ? word.slice(1) : word, MAX_SUGGESTIONS)

  if (!found.length) return closeSuggestions()

  wordRange = { start: caret - word.length, end: caret }
  suggestions.value = found
  activeIndex.value = -1
}

const applySuggestion = (item) => {
  if (!wordRange || !item) return

  const { start, end } = wordRange
  const word = item.nick ? `@${item.name}` : item.name
  // Пробіл після назви обовʼязковий: чат шукає і емоцію, і згадку як ціле
  // слово, тож «Kappaякось» лишиться текстом, а «@нікщось» — не звертанням
  text.value = `${text.value.slice(0, start)}${word} ${text.value.slice(end)}`
  const caret = start + word.length + 1

  closeSuggestions()
  nextTick(() => {
    area.value?.focus()
    area.value?.setSelectionRange(caret, caret)
  })
}

/**
 * Згадати когось із стрічки: клік по ніку в повідомленні дописує «@нік» у
 * поле. Кличе ChatLiveBody
 */
const mention = (name) => {
  if (!name) return
  const separator = !text.value || /\s$/.test(text.value) ? '' : ' '
  text.value = `${text.value}${separator}@${name} `.slice(0, props.maxLength)
  nextTick(() => area.value?.focus())
}

const focusInput = () => area.value?.focus()

// Вибрали, на що відповісти, — курсор одразу в поле: інакше після кліку
// по кнопці довелося б ще раз клікати в текст
watch(
  () => props.replyTo,
  (target) => {
    if (target) nextTick(focusInput)
  }
)

defineExpose({ mention, focus: focusInput })

// Смайл із панелі вибору дописуємо в кінець — саме там курсор у
// переважній більшості випадків, а перестрибувати за ним панель не мусить
const appendEmote = (name) => {
  const separator = !text.value || /\s$/.test(text.value) ? '' : ' '
  text.value = `${text.value}${separator}${name} `.slice(0, props.maxLength)
  nextTick(() => area.value?.focus())
}

// Гіфка з панелі — окремим повідомленням одразу, як у Telegram: набраний
// текст і вкладення лишаються в полі. Відповідь, якщо її почали, іде разом
// з гіфкою — відповідати гіфкою якраз і хочуть
const sendGif = async (url) => {
  if (!props.connected || props.muted || sending.value) return
  failure.value = ''
  sending.value = true
  let sent
  try {
    sent = await props.send({ text: url, mediaIds: [], spoiler: false, media: [], replyTo: props.replyTo })
  } catch (e) {
    failure.value = e?.message || 'Не вдалося надіслати'
    return
  } finally {
    sending.value = false
  }
  if (sent === false) {
    failure.value = 'Немає звʼязку з чатом — спробуйте ще раз'
    return
  }
  emit('cancel-reply')
}

// Спойлер у тексті: обгортає виділене в ||…||, а без виділення ставить
// порожню пару й курсор усередину
const wrapSpoiler = () => {
  const el = area.value
  const value = text.value
  const from = el?.selectionStart ?? value.length
  const to = el?.selectionEnd ?? value.length

  text.value = `${value.slice(0, from)}||${value.slice(from, to)}||${value.slice(to)}`.slice(0, props.maxLength)

  nextTick(() => {
    el?.focus()
    el?.setSelectionRange(from + 2, to + 2)
  })
}

// Швидкий перехід у поле: «/» або «.» з будь-якого місця сторінки ставлять
// курсор у чат — так само, як у Twitch чи YouTube. Саме ці дві клавіші, бо
// плеєр розбирає свої гарячі клавіші за e.code (Space, K, M, C, F, стрілки),
// і Slash із Period там вільні.
//
// Символ у поле не підставляємо: команд у чаті немає, і «/» на початку
// кожного повідомлення довелося б стирати руками
const FOCUS_KEYS = ['Slash', 'Period', 'NumpadDivide', 'NumpadDecimal']

const onGlobalKey = (event) => {
  if (!FOCUS_KEYS.includes(event.code)) return
  // Комбінації не наші: Cmd+/ у браузері свої, а Ctrl+. чіпати не варто
  if (event.ctrlKey || event.metaKey || event.altKey) return

  // Людина вже пише — у поле пошуку смайлів, у чат чи будь-де ще
  const tag = event.target?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return

  const el = area.value
  if (!el) return

  event.preventDefault()

  // Згорнутий чат розгортаємо: закритий він не зникає з дерева, а
  // схлопується до нульової ширини (інакше фрейм Twitch перезавантажувався
  // б на кожне відкриття) — тобто поле є, але писати в нього наосліп.
  // Якщо чат і так відкритий, це нічого не міняє
  emit('open-chat')
  nextTick(() => el.focus())
}

onMounted(() => {
  if (props.globalHotkey) window.addEventListener('keydown', onGlobalKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKey)
  for (const item of attachments.value) URL.revokeObjectURL(item.preview)
})

// --- Медіа ---
const pickFile = () => fileInput.value?.click()

const isVideoFile = (file) => /^video\//i.test(file?.type || '')

/**
 * Один шлях для всіх трьох способів прикріпити: кнопка, Ctrl+V і кидання
 * мишею. Правила ті самі, що й на сервері: до пʼяти картинок або одне відео
 * саме по собі — зайве не беремо й кажемо чому
 */
const attachFiles = (fileList) => {
  const files = [...(fileList || [])].filter(Boolean)
  if (!files.length) return

  failure.value = ''

  const hasVideo = attachments.value.some((item) => item.video)
  const addingVideo = files.some(isVideoFile)

  if (hasVideo || (addingVideo && (attachments.value.length || files.length > 1))) {
    failure.value = 'Відео надсилається окремо — без інших вкладень'
    return
  }

  const room = props.maxMedia - attachments.value.length
  if (room <= 0) {
    failure.value = `Не більше ${props.maxMedia} вкладень в одному повідомленні`
    return
  }
  if (files.length > room) {
    failure.value = `Не більше ${props.maxMedia} вкладень — зайві не додано`
  }

  // Медіа в чаті частіше ховають, ніж ні: там і спойлери до серій, і те,
  // що не всім варто бачити зненацька. Вмикаємо з першим вкладенням
  if (!attachments.value.length) spoiler.value = true

  for (const file of files.slice(0, room)) {
    const item = reactive({
      key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      preview: URL.createObjectURL(file),
      video: isVideoFile(file),
      status: 'uploading',
      media: null
    })
    attachments.value = [...attachments.value, item]

    props
      .upload(file)
      .then((media) => {
        item.media = media
        item.status = 'done'
      })
      .catch((e) => {
        failure.value = e.message || 'Не вдалося завантажити файл'
        dropAttachment(item.key)
      })
  }
}


const onFile = (event) => {
  const files = [...(event.target.files || [])]
  // Поле скидаємо одразу: інакше повторний вибір того самого файлу не
  // викличе change
  event.target.value = ''
  attachFiles(files)
}

const dropAttachment = (key) => {
  const item = attachments.value.find((entry) => entry.key === key)
  if (item) URL.revokeObjectURL(item.preview)
  attachments.value = attachments.value.filter((entry) => entry.key !== key)
  if (!attachments.value.length) spoiler.value = false
}

const onPaste = (event) => {
  const items = [...(event.clipboardData?.items || [])]
  const files = items
    .filter((item) => item.kind === 'file' && /^image\//i.test(item.type || ''))
    .map((item) => item.getAsFile())
    .filter(Boolean)

  if (!files.length) return

  event.preventDefault()
  attachFiles(files)
}

// Кидання мишею — те саме, що й Ctrl+V, тільки з файлового менеджера.
// dragover обовʼязково з preventDefault: без нього браузер просто відкриє
// кинуту картинку замість сторінки
const onDragOver = (event) => {
  if (!event.dataTransfer?.types?.includes('Files')) return
  event.preventDefault()
  dragOver.value = true
}

const onDragLeave = (event) => {
  // Перехід між дочірніми елементами теж стріляє dragleave — реагуємо
  // тільки на вихід за межі самої смуги
  if (event.currentTarget.contains(event.relatedTarget)) return
  dragOver.value = false
}

const onDrop = (event) => {
  if (!event.dataTransfer?.files?.length) return
  event.preventDefault()
  dragOver.value = false
  attachFiles(event.dataTransfer.files)
}

const clearAttachments = () => {
  // Превʼю вже не потрібні — повідомлення поїхало з серверними адресами
  for (const item of attachments.value) URL.revokeObjectURL(item.preview)
  attachments.value = []
  spoiler.value = false
}

// --- Надсилання ---
const canSend = computed(
  () =>
    props.connected &&
    !props.muted &&
    !uploading.value &&
    !sending.value &&
    (!!text.value.trim() || ready.value.length > 0)
)

const submit = async () => {
  if (!canSend.value) return

  // Попередня скарга («завелике медіа», «немає звʼязку») стосувалась
  // попередньої спроби — з новою вона вже не про що
  failure.value = ''

  const payload = {
    text: text.value.trim(),
    mediaIds: ready.value.map((item) => item.media.id),
    spoiler: spoiler.value,
    // Медіа передаємо цілим: рядок стає в стрічку одразу, ще до відповіді
    // сервера, і картинки в ньому треба з чогось намалювати
    media: ready.value.map((item) => item.media),
    replyTo: props.replyTo
  }

  let sent
  sending.value = true
  try {
    sent = await props.send(payload)
  } catch (e) {
    failure.value = e?.message || 'Не вдалося надіслати'
    return
  } finally {
    sending.value = false
  }

  // Зʼєднання могло відвалитись рівно між натисканням і відправкою —
  // тоді набране лишається в полі, а не зникає в нікуди
  if (sent === false) {
    failure.value = 'Немає звʼязку з чатом — спробуйте ще раз'
    return
  }

  text.value = ''
  clearAttachments()
  closeSuggestions()
  emit('cancel-reply')
}

// Сервер не прийняв (або не відповів): бліде повідомлення зникло зі
// стрічки — повертаємо набране в поле, щоб його можна було просто
// надіслати ще раз. Поверх уже набраного нового не пишемо
watch(
  () => props.rejected,
  (failed) => {
    if (!failed) return
    failure.value = failed.reason || 'Повідомлення не надіслано'
    if (!text.value.trim() && failed.text) {
      text.value = failed.text
      nextTick(() => area.value?.focus())
    }
  }
)

const onKeydown = (event) => {
  if (suggestions.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      activeIndex.value = (activeIndex.value + 1) % suggestions.value.length
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      activeIndex.value =
        activeIndex.value <= 0 ? suggestions.value.length - 1 : activeIndex.value - 1
      return
    }
    // Tab підставляє те, що зверху списку, — навіть якщо стрілками нікуди
    // не ходили
    if (event.key === 'Tab') {
      event.preventDefault()
      applySuggestion(suggestions.value[Math.max(activeIndex.value, 0)])
      return
    }
    // Enter підставляє тільки свідомо вибране стрілками, інакше — звичайне
    // надсилання нижче
    if (event.key === 'Enter' && !event.shiftKey && activeIndex.value >= 0) {
      event.preventDefault()
      applySuggestion(suggestions.value[activeIndex.value])
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      closeSuggestions()
      return
    }
  }

  // Escape при відкритій відповіді скасовує саме її: підказок уже немає
  // (їх Escape закрив вище), а тікати з поля людині нікуди
  if (event.key === 'Escape' && props.replyTo) {
    event.preventDefault()
    emit('cancel-reply')
    return
  }

  // Enter надсилає, Shift+Enter переносить рядок — як у будь-якому чаті
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    submit()
  }
}

const mutedHint = computed(() => {
  if (!props.mutedUntil) return 'Вам заборонено писати в чат'
  const until = new Date(props.mutedUntil)
  if (Number.isNaN(until.getTime())) return 'Вам заборонено писати в чат'
  return `Ви не можете писати до ${until.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit'
  })}`
})

// Вхід — той самий, що й у гейта ефіру: провайдер треба запамʼятати для
// /auth/callback, бо Strapi не каже, чий токен повернувся
const { getProviderAuthenticationUrl } = useStrapiAuth()
const route = useRoute()

const login = (provider) => {
  useCookie('auth_provider', { path: '/' }).value = provider
  useCookie('redirect', { path: '/' }).value = props.loginRedirect || route.fullPath
  window.location.href = getProviderAuthenticationUrl(provider)
}
</script>

<template>
  <div
    class="relative flex-shrink-0 bg-base-200 border-base-content/10 border-t transition-colors"
    :class="{ 'bg-primary/10': dragOver }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Над полем тримають файл: у чаті, що біжить, без підказки незрозуміло,
         чи його тут узагалі візьмуть -->
    <div
      v-if="dragOver && authenticated"
      class="z-40 absolute inset-0 flex justify-center items-center bg-base-200/90 border-2 border-primary border-dashed rounded pointer-events-none"
    >
      <span class="text-[11px] text-primary">Відпустіть — прикріпимо до повідомлення (до {{ maxMedia }})</span>
    </div>

    <!-- Не залогінений: писати нема від чийого імені -->
    <div
      v-if="!authenticated"
      class="flex flex-wrap justify-center items-center gap-2 p-2 text-[11px] text-base-content/60"
    >
      <span>Увійдіть, щоб писати</span>
      <button
        class="btn btn-xs btn-primary"
        @click="login('twitch')"
      >
        Twitch
      </button>
      <button
        class="btn btn-xs"
        @click="login('discord')"
      >
        Discord
      </button>
    </div>

    <template v-else>
      <!--
        На що відповідаємо. Смужка живе над полем, доки не надіслали або не
        скасували (хрестик чи Escape) — інакше на довгій відповіді легко
        забути, кому саме пишеш
      -->
      <div
        v-if="replyTo"
        class="flex items-center gap-1.5 bg-base-300/60 px-2 py-1 border-primary border-l-2 text-[11px]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="opacity-50 shrink-0"
        >
          <polyline points="9 17 4 12 9 7" />
          <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
        </svg>

        <span class="text-base-content/50 shrink-0">Відповідь</span>
        <span
          class="font-semibold shrink-0"
          :style="{ color: nickColor(replyTo.author) }"
          >{{ replyTo.author }}</span
        >
        <span class="text-base-content/50 truncate">{{
          replyTo.text || (replyTo.media ? 'медіа' : '')
        }}</span>

        <button
          class="ml-auto text-base-content/50 hover:text-error shrink-0"
          title="Скасувати відповідь (Esc)"
          @click="emit('cancel-reply')"
        >
          ✕
        </button>
      </div>

      <!-- Вкладення: превʼю кожного, хрестик на кожному і один спойлер на
           всю пачку -->
      <div
        v-if="attachments.length"
        class="flex flex-wrap items-center gap-1.5 px-2 pt-2"
      >
        <div
          v-for="item in attachments"
          :key="item.key"
          class="group/att relative bg-base-300 rounded w-12 h-12 overflow-hidden shrink-0"
        >
          <video
            v-if="item.video"
            :src="item.preview"
            class="w-full h-full object-cover"
            muted
          />
          <img
            v-else
            :src="item.preview"
            class="w-full h-full object-cover"
            :class="{ 'blur-sm': spoiler }"
            alt=""
          />
          <span
            v-if="item.status === 'uploading'"
            class="absolute inset-0 flex justify-center items-center bg-base-300/70"
          >
            <span class="loading loading-spinner loading-xs" />
          </span>
          <button
            type="button"
            class="top-0 right-0 absolute flex justify-center items-center bg-base-300/90 hover:bg-error rounded-bl w-4 h-4 text-[10px] hover:text-error-content leading-none"
            title="Прибрати"
            @click="dropAttachment(item.key)"
          >
            ✕
          </button>
        </div>

        <span class="text-[10px] text-base-content/40 tabular-nums">{{ attachments.length }}/{{ maxMedia }}</span>

        <label class="flex items-center gap-1.5 ml-auto text-[11px] cursor-pointer">
          <input
            v-model="spoiler"
            type="checkbox"
            class="checkbox checkbox-xs checkbox-primary"
          />
          Під спойлером
        </label>
      </div>

      <p
        v-if="failure"
        class="px-2 pt-1 text-[11px] text-error"
      >
        {{ failure }}
      </p>

      <p
        v-else-if="muted"
        class="px-2 pt-1 text-[11px] text-warning"
      >
        {{ mutedHint }}
      </p>

      <!--
        Кнопки й поле — однакові 32 пікселі заввишки, тож у порожньому чаті
        ряд читається одним рядком. Вирівнювання по низу, а не по центру:
        коли текст переростає в кілька рядків, кнопки лишаються навпроти
        останнього, а не з'їжджають на середину поля
      -->
      <div class="relative flex items-end gap-1 p-2">
        <!-- Підказки смайлів. Над полем, бо саме поле — унизу вікна -->
        <div
          v-if="suggestions.length"
          class="bottom-full left-2 z-50 absolute bg-base-200 shadow-xl mb-1 border border-base-content/10 rounded-lg w-[min(260px,80%)] max-h-[220px] overflow-y-auto"
        >
          <button
            v-for="(item, index) in suggestions"
            :key="(item.nick ? '@' : '') + item.name"
            type="button"
            class="flex items-center gap-2 px-2 py-1 w-full text-left"
            :class="index === activeIndex ? 'bg-primary/25' : 'hover:bg-base-300'"
            @mouseenter="activeIndex = index"
            @mousedown.prevent="applySuggestion(item)"
          >
            <!-- Нік підказуємо тим самим кольором, яким він підписаний у
                 стрічці: у списку з восьми імен це швидше за читання -->
            <span
              v-if="item.nick"
              class="font-semibold text-xs truncate"
              :style="{ color: nickColor(item.name) }"
              >@{{ item.name }}</span
            >

            <template v-else>
              <img
                :src="item.url"
                :alt="item.name"
                class="max-w-6 max-h-6 object-contain"
              />
              <span class="text-xs truncate">{{ item.name }}</span>
              <span class="ml-auto text-[10px] text-base-content/40 uppercase">{{
                item.provider
              }}</span>
            </template>
          </button>

          <p
            class="bottom-0 sticky bg-base-300 px-2 py-0.5 text-[10px] text-base-content/45"
          >
            Tab — підставити, ↑↓ — вибрати, «:» — лише смайли
          </p>
        </div>

        <ChatEmotePicker
          @pick="appendEmote"
          @gif="sendGif"
        />

        <button
          type="button"
          class="btn btn-ghost btn-sm btn-circle"
          title="Спойлер: ||текст|| — розмитий, поки не натиснуть"
          @click="wrapSpoiler"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        </button>

        <button
          class="btn btn-ghost btn-sm btn-circle"
          :disabled="attachments.length >= maxMedia || attachments.some((item) => item.video)"
          :title="
            attachments.length >= maxMedia
              ? `Не більше ${maxMedia} вкладень`
              : `До ${maxMedia} картинок або одне відео (можна Ctrl+V і перетягнути)`
          "
          @click="pickFile"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
            />
          </svg>
        </button>

        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp,image/avif,image/bmp,video/mp4,video/webm,video/quicktime"
          @change="onFile"
        />

        <textarea
          ref="area"
          v-model="text"
          rows="1"
          :maxlength="maxLength"
          :placeholder="connected ? placeholder : 'Підключаємось…'"
          class="flex-1 bg-base-300 px-2 py-1.5 rounded min-h-[32px] max-h-24 text-sm resize-none"
          @input="refreshSuggestions"
          @click="refreshSuggestions"
          @keydown="onKeydown"
          @paste="onPaste"
          @blur="closeSuggestions"
        />

        <button
          class="btn btn-primary btn-sm btn-circle"
          :class="{ loading: sending }"
          :disabled="!canSend"
          title="Надіслати"
          @click="submit"
        >
          <!-- Звичайний «літачок» суцільною заливкою — така сама кнопка
               надсилання, як у будь-якому месенджері. Контурний варіант на
               32 пікселях читався як порожній трикутник -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>
